import { cn } from '@iq/ui';
import type { ComponentPropsWithoutRef, ElementType } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface SourceMarkdownPreviewProps {
  content: string;
}

/**
 * Intrinsic-element props for a markdown component override. react-markdown's
 * `Components` type is a union that doesn't contextually type the override
 * params, so each override annotates its props explicitly (gives `className` a
 * type instead of an implicit `any`).
 */
type El<Tag extends ElementType> = ComponentPropsWithoutRef<Tag>;

export function SourceMarkdownPreview({ content }: SourceMarkdownPreviewProps) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
}

const markdownComponents: Components = {
  h1: ({ className, ...props }: El<'h1'>) => (
    <h1
      className={cn(
        'mb-4 border-b pb-3 text-2xl font-semibold tracking-tight text-foreground first:mt-0',
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }: El<'h2'>) => (
    <h2
      className={cn(
        'mt-8 mb-3 border-b pb-2 text-xl font-semibold tracking-tight text-foreground first:mt-0',
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }: El<'h3'>) => (
    <h3
      className={cn('mt-6 mb-2 text-lg font-semibold tracking-tight text-foreground', className)}
      {...props}
    />
  ),
  h4: ({ className, ...props }: El<'h4'>) => (
    <h4 className={cn('mt-5 mb-2 text-base font-semibold text-foreground', className)} {...props} />
  ),
  h5: ({ className, ...props }: El<'h5'>) => (
    <h5 className={cn('mt-4 mb-2 text-sm font-semibold text-foreground', className)} {...props} />
  ),
  h6: ({ className, ...props }: El<'h6'>) => (
    <h6
      className={cn(
        'mt-4 mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground',
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }: El<'p'>) => (
    <p
      className={cn('my-3 leading-7 text-foreground first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  a: ({ className, ...props }: El<'a'>) => (
    <a
      className={cn(
        'font-medium text-secondary-foreground underline decoration-secondary-foreground/30 underline-offset-4 transition-colors hover:text-secondary-foreground/80 hover:decoration-secondary-foreground/70',
        className,
      )}
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  ul: ({ className, ...props }: El<'ul'>) => (
    <ul
      className={cn('my-3 ms-5 list-disc space-y-1.5 marker:text-muted-foreground', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }: El<'ol'>) => (
    <ol
      className={cn('my-3 ms-5 list-decimal space-y-1.5 marker:text-muted-foreground', className)}
      {...props}
    />
  ),
  li: ({ className, ...props }: El<'li'>) => (
    <li className={cn('pl-1 leading-7', className)} {...props} />
  ),
  blockquote: ({ className, ...props }: El<'blockquote'>) => (
    <blockquote
      className={cn(
        'my-5 rounded-r-xl border-l-4 border-secondary-foreground/30 bg-muted/40 px-4 py-3 text-muted-foreground italic',
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }: El<'hr'>) => (
    <hr className={cn('my-7 border-border', className)} {...props} />
  ),
  table: ({ className, ...props }: El<'table'>) => (
    <div className="my-5 overflow-x-auto rounded-xl border bg-background">
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  ),
  thead: ({ className, ...props }: El<'thead'>) => (
    <thead className={cn('bg-muted/70', className)} {...props} />
  ),
  th: ({ className, ...props }: El<'th'>) => (
    <th className={cn('border-b px-3 py-2 text-left font-semibold', className)} {...props} />
  ),
  td: ({ className, ...props }: El<'td'>) => (
    <td className={cn('border-b px-3 py-2 align-top last:border-r-0', className)} {...props} />
  ),
  tr: ({ className, ...props }: El<'tr'>) => (
    <tr className={cn('last:[&>td]:border-b-0', className)} {...props} />
  ),
  pre: ({ className, ...props }: El<'pre'>) => (
    <pre
      className={cn(
        'my-4 overflow-x-auto rounded-xl border bg-muted/40 p-4 text-xs leading-relaxed text-foreground',
        className,
      )}
      {...props}
    />
  ),
  code: ({ className, ...props }: El<'code'>) => (
    <code
      className={cn(
        'rounded-md border border-border/60 bg-muted/60 px-1.5 py-0.5 font-mono text-[0.86em]',
        className,
      )}
      {...props}
    />
  ),
  img: () => null,
};
